import { type MotionValue } from 'motion/react'
import { type ReactNode } from 'react'
import { Avatar } from '@/components/Avatar'

import { IconButton } from '@/components/IconButton'
import { ContentLayout } from '@/components/ContentLayout'
import { Icon } from '@/components/Icon'
import {
  ModalSheet,
  ModalSheetContent,
  ModalSheetHeader,
} from '@/components/ModalSheet'
import {
  StickyViewHeader,
  StickyViewHeaderExtras,
  StickyViewHeaderMain,
} from '@/components/StickyViewHeader'
import { TableHost } from '@/components/Table'
import { TableScroller } from '@/components/Table/TableScroller'
import { useScrollOffsets } from '@/hooks/element-hooks'
import { TableSearchbar } from '@/components/TableSearchbar'
import styles from './ApplicantManagementPanel.module.css'
import { Dropdown, DropdownItem, DropdownTitle } from '@/components/Dropdown'
import { Tooltip } from '@/components/Tooltip'

export interface ApplicantManagementPanelProps {
  showing: boolean
  onClose: () => unknown
  postingId?: string
  workspaceName?: string
  workspaceIcon?: string
  totalApplicants?: number
  searchText: string
  onSearchTextChange: (text: string) => void
  onInviteSelected: () => unknown
  isInviteSelectedDisabled: boolean
  onRescindSelected: () => unknown
  isRescindSelectedDisabled: boolean
  onExportCsv: () => unknown
  /**
   * The applicants table.
   */
  children?: ReactNode
}

export function ApplicantManagementPanel({
  showing,
  onClose,
  postingId,
  workspaceName,
  workspaceIcon,
  totalApplicants,
  searchText,
  onSearchTextChange,
  onInviteSelected,
  isInviteSelectedDisabled,
  onRescindSelected,
  isRescindSelectedDisabled,
  onExportCsv,
  children,
}: ApplicantManagementPanelProps) {
  const { connectRef: scrollRef, scrollYStart: scrollOffset } =
    useScrollOffsets()

  return (
    <ModalSheet
      panel
      showing={showing}
      onRequestClose={onClose}
      disableCloseOnOutsideClick
    >
      <ModalSheetHeader withCloseButton />
      <ModalSheetContent expandable>
        <div className={styles.root} ref={scrollRef}>
          <ApplicantManagementPanelHeader
            workspaceName={workspaceName}
            workspaceIcon={workspaceIcon}
            scrollOffset={scrollOffset}
          />
          <ApplicantManagementPanelContent
            postingId={postingId}
            totalApplicants={totalApplicants}
            searchText={searchText}
            onSearchTextChange={onSearchTextChange}
            onInviteSelected={onInviteSelected}
            isInviteSelectedDisabled={isInviteSelectedDisabled}
            onRescindSelected={onRescindSelected}
            isRescindSelectedDisabled={isRescindSelectedDisabled}
            onExportCsv={onExportCsv}
          >
            {children}
          </ApplicantManagementPanelContent>
        </div>
      </ModalSheetContent>
    </ModalSheet>
  )
}

interface ApplicantManagementPanelHeaderProps {
  workspaceName?: string
  workspaceIcon?: string
  scrollOffset: MotionValue<number>
}

function ApplicantManagementPanelHeader({
  workspaceName,
  workspaceIcon,
  scrollOffset,
}: ApplicantManagementPanelHeaderProps) {
  return (
    <StickyViewHeader scrollOffset={scrollOffset} noTopPadding>
      <StickyViewHeaderMain>
        <div className={styles.headerMainContainer}>
          <Avatar
            size={48}
            square
            subject={{
              displayName: workspaceName,
              avatar: workspaceIcon,
            }}
          />
          <div className={styles.headerTitle}>
            <p className={styles.subtitleText}>Applicant manager</p>
            <h3 className={styles.titleText}>{workspaceName}</h3>
          </div>
        </div>
      </StickyViewHeaderMain>
      <StickyViewHeaderExtras />
    </StickyViewHeader>
  )
}

interface ApplicantManagementPanelContentProps {
  postingId?: string
  totalApplicants?: number
  searchText: string
  onSearchTextChange: (text: string) => void
  onInviteSelected: () => unknown
  isInviteSelectedDisabled: boolean
  onRescindSelected: () => unknown
  isRescindSelectedDisabled: boolean
  onExportCsv: () => unknown
  children?: ReactNode
}

function ApplicantManagementPanelContent({
  postingId,
  totalApplicants,
  searchText,
  onSearchTextChange,
  onInviteSelected,
  isInviteSelectedDisabled,
  onRescindSelected,
  isRescindSelectedDisabled,
  onExportCsv,
  children,
}: ApplicantManagementPanelContentProps) {
  const testId = 'applicant-manager'

  return (
    <div className={styles.content}>
      <div className={styles.applicantCountContainer}>
        Applicants
        <span className={styles.applicantCount}>({totalApplicants ?? 0})</span>
      </div>
      <ContentLayout>
        <TableHost>
          <TableSearchbar
            searchText={searchText}
            onSearchTextChange={onSearchTextChange}
          >
            <Dropdown
              autoDismiss
              width="miniscule"
              position="bottom-end"
              button={
                <Tooltip content={'Menu'} placement="bottom">
                  <IconButton
                    name="more"
                    label="Bulk actions"
                    testId={`${testId}-bulk-actions-open-${postingId}`}
                    color="var(--grey600)"
                  />
                </Tooltip>
              }
            >
              <div>
                <DropdownTitle>Bulk actions</DropdownTitle>
                <DropdownItem
                  onPress={onInviteSelected}
                  isDisabled={isInviteSelectedDisabled}
                  buttonTestId={`${testId}-bulk-actions-invite-${postingId}`}
                >
                  Invite selected
                </DropdownItem>
                <DropdownItem
                  onPress={onRescindSelected}
                  isDisabled={isRescindSelectedDisabled}
                  buttonTestId={`${testId}-bulk-actions-rescind-invitation-${postingId}`}
                >
                  Rescind selected
                </DropdownItem>
                <DropdownItem
                  onPress={onExportCsv}
                  icon={<Icon name="download" size={24} />}
                  buttonTestId={`${testId}-export-csv-${postingId}`}
                >
                  Export CSV
                </DropdownItem>
              </div>
            </Dropdown>
          </TableSearchbar>
          <TableScroller>{children}</TableScroller>
        </TableHost>
      </ContentLayout>
    </div>
  )
}
